import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { ConsultThread } from '../medical/ConsultThread';
import { NotesDialog } from '../medical/ProviderTools';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Eye, Search, RefreshCw, StickyNote } from 'lucide-react';

// V14.6 — Superadmin SILENT read-only oversight of nutritionist consultations.
// Moved here under SoulAI (was on the standalone Medical Team page). Opening a
// transcript notifies no one; the view is audit-logged server-side.
export function ConsultationsOversight() {
  const [consults, setConsults] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [notesFor, setNotesFor] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get('/api/admin/medical/consults')
      .then(c => setConsults(c.data.consults || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const s = search.trim().toLowerCase();
  const rows = s
    ? consults.filter(c => (c.learner_name || '').toLowerCase().includes(s) || (c.concern || '').toLowerCase().includes(s))
    : consults;

  return (
    <Card className="rounded-2xl" data-testid="consultations-oversight">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Eye className="h-4 w-4" />Consultations (read-only)</CardTitle>
            <CardDescription>Silent oversight — opening a transcript does not notify anyone.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search learner / concern…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 w-full sm:w-56" data-testid="consult-search-input" />
            </div>
            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={load} data-testid="consult-refresh-btn"><RefreshCw className="h-4 w-4" /></Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{consults.length === 0 ? 'No consultations yet.' : 'No consultations match your search.'}</p>
        ) : rows.map(c => (
          <div key={c.id} className="flex items-center justify-between p-3 rounded-xl border text-sm" data-testid="consult-row">
            <div className="min-w-0">
              <p className="font-medium truncate">{c.learner_name} <span className="text-muted-foreground">· {c.concern || '—'}</span></p>
              <p className="text-xs text-muted-foreground">{c.message_count} msgs{c.rating?.score_out_of_10 ? ` · ★${c.rating.score_out_of_10}/10` : ''}{c.first_response_time_seconds != null ? ` · accept ${Math.round(c.first_response_time_seconds / 60)}m` : ''}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant={c.status === 'active' ? 'default' : c.status === 'waiting' ? 'secondary' : 'outline'} className="capitalize">{c.status}</Badge>
              <Button variant="ghost" size="sm" onClick={() => setViewing(c.id)} data-testid="view-transcript-btn"><Eye className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </CardContent>

      {/* Read-only transcript modal */}
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="max-w-lg h-[70vh] flex flex-col p-0">
          <DialogHeader className="p-4 pb-2 flex flex-row items-start justify-between gap-2">
            <div><DialogTitle className="text-base">Transcript (read-only)</DialogTitle><DialogDescription>Silent oversight — neither the learner nor the nutritionist is notified.</DialogDescription></div>
            {viewing && <Button variant="outline" size="sm" className="shrink-0 mr-6" onClick={() => setNotesFor(viewing)} data-testid="admin-consult-notes-btn"><StickyNote className="h-4 w-4 mr-1" />Internal notes</Button>}
          </DialogHeader>
          <div className="flex-1 min-h-0">
            {viewing && <ConsultThread consultId={viewing} fetchUrl={`/api/admin/medical/consult/${viewing}`} composer={false} />}
          </div>
        </DialogContent>
      </Dialog>
      {notesFor && <NotesDialog consultId={notesFor} open={!!notesFor} onOpenChange={(v) => !v && setNotesFor(null)} canAdd={false} />}
    </Card>
  );
}
